import {
  Divider,
  FieldMessage,
  GoogleMark,
  MicrosoftMark,
  ProviderButton,
  useLanguage,
  useTranslate,
} from '@athena/ui'
import { useEffect, useState, type ReactNode } from 'react'

import {
  fetchSocialProviders,
  SOCIAL_PROVIDERS,
  startProviderSignIn,
  type SocialProviderId,
} from './providers.js'

/** The mark to draw beside each provider's name, already built. */
const MARKS: Record<SocialProviderId, ReactNode> = {
  google: <GoogleMark />,
  microsoft: <MicrosoftMark />,
}

const LABEL_KEYS = {
  google: 'auth.continueWithGoogle',
  microsoft: 'auth.continueWithMicrosoft',
} as const

export interface SocialSignInProps {
  /**
   * Where the provider should land the person, as an app path with no language
   * prefix. The prefix is added from the page's own language, so a sign-in
   * started on `/en/invite` comes back to `/en/invite`.
   */
  callbackPath?: string
}

/**
 * "Continue with Google" and "Continue with Microsoft" — the ones this
 * deployment can actually finish.
 *
 * ── Only what works is offered ───────────────────────────────────────────
 *
 * `/api/auth-methods` lists the providers the server holds credentials for,
 * and a provider that is not on it is not on the page. A button with no
 * client secret behind it would send the person to a real consent screen and
 * fail only after they had approved; a disabled one said "not available on
 * this server", which is a sentence about infrastructure on the one page where
 * a visitor is deciding whether to trust us. Neither is worth showing. The
 * code form below always works, so nobody is left without a way in.
 *
 * Until the server answers, both render as they will most likely be, so the
 * form does not jump down when the answer arrives; one the answer leaves out
 * is then removed.
 *
 * ── Why `window.location.assign` and not a router navigation ─────────────
 *
 * The destination is accounts.google.com. TanStack Router routes within this
 * app; leaving it is the browser's job. A full navigation is also what makes
 * the OAuth state cookie the API just set visible on the way back, and what
 * discards this tab's React state before the person returns with a session
 * that may belong to somebody else.
 */
export function SocialSignIn({ callbackPath = '/' }: SocialSignInProps) {
  const t = useTranslate()
  const language = useLanguage()
  /**
   * Which providers this deployment can complete.
   *
   * `undefined` until the server answers, which is not the same as "none": the
   * buttons render either way, and this only decides whether they are live.
   * Starting undefined rather than empty is what stops them flashing disabled
   * for the moment before the answer arrives.
   */
  const [configured, setConfigured] = useState<SocialProviderId[]>()
  const [pending, setPending] = useState<SocialProviderId>()
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let live = true
    void fetchSocialProviders().then((available) => {
      if (live) setConfigured(available)
    })
    // The answer can arrive after the person has navigated away, and setting
    // state on a gone component is a warning nobody can act on.
    return () => {
      live = false
    }
  }, [])

  async function start(provider: SocialProviderId) {
    setPending(provider)
    setFailed(false)

    const url = await startProviderSignIn({ provider, language, callbackPath })
    if (!url) {
      setPending(undefined)
      setFailed(true)
      return
    }
    window.location.assign(url)
  }

  /** Unknown until the server answers, and offered from then on only if it is listed. */
  const offered = SOCIAL_PROVIDERS.filter(
    (provider) => configured === undefined || configured.includes(provider),
  )
  if (offered.length === 0) return null

  return (
    <div className="flex flex-col gap-3">
      {offered.map((provider) => (
        <ProviderButton
          key={provider}
          size="lg"
          mark={MARKS[provider]}
          label={t(LABEL_KEYS[provider])}
          loading={pending === provider}
          // Not pressable while another provider is redirecting: two
          // handshakes at once is one session nobody asked for.
          disabled={pending !== undefined && pending !== provider}
          onClick={() => void start(provider)}
        />
      ))}

      {failed ? <FieldMessage tone="error">{t('auth.providerFailed')}</FieldMessage> : null}

      {/* What says the buttons and the code form are alternatives rather
          than steps: without it a form under two buttons reads as "and then". */}
      <div className="mt-3 flex items-center gap-4" aria-hidden="true">
        <Divider className="flex-1" />
        <span className="text-body-sm text-text-muted">{t('auth.providerDivider')}</span>
        <Divider className="flex-1" />
      </div>
    </div>
  )
}
